import React, { useState, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  List,
  ListItem,
  Link,
  Textarea,
  Button,
  Icon,
} from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import PrimarySideHeader from "../../../components/PrimarySideHeader";
import PrimaryButton from "../../../components/PrimaryButton";

const ReportsViewModal = ({
  isOpen,
  onClose,
  reports,
  hcfRemarks,
  ipsRemarks,
  doctorProvidedOpinion = false,
  handleReportsModalSubmit = () => {},
  downloadFiles = () => {},
}) => {
  const [additionalReports, setAdditionalReports] = useState("");
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const getFileName = (url) => {
    return url.split("/").pop();
  };

  const handleAddReports = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setAttachments(prevAttachments => [...prevAttachments, ...newFiles]);
      
      // Reset the file input to allow selecting the same files again if needed
      e.target.value = null;
    }
  };

  const handleSubmit = () => {
    // Pass both the remarks text and file attachments to the parent component
    console.log("attachments=", attachments);
    console.log("additionalReports=", additionalReports);
    handleReportsModalSubmit(additionalReports, attachments);
    
    // Reset form state
    setAdditionalReports("");
    setAttachments([]);
    
    // Close the modal
    onClose();
  };

  const downloadAllReports = () => {
    let fileType = "patient_reports";
    downloadFiles(fileType);
  };

  const handleRemoveFile = (index) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };

  const renderRequestedReport = (report) => {
    switch (report.mime_type) {
      case "Text":
        return <Text>{report.opinion}</Text>;
      case "Image":
      case "Audio":
        return (
          <List spacing={2}>
            {report.opinion.split(",").map((fileUrl, idx) => (
              <ListItem key={idx}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Text fontSize="lg">{getFileName(fileUrl)}</Text>
                  <Link
                    href={fileUrl}
                    isExternal
                    color="blue.500"
                    fontWeight="bold"
                    textDecoration="underline"
                  >
                    View
                  </Link>
                </Box>
              </ListItem>
            ))}
          </List>
        );
      default:
        return <Text>{report.opinion}</Text>;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="primary.1" textAlign={"center"} fontSize={"2xl"}>
          <Text>Reports</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <PrimaryButton
            mb="20px"
            onClick={downloadAllReports}
            display={"flex"}
            justifyContent={"flex-end"}
            ml="auto"
          >
            Download Reports
          </PrimaryButton>
          {reports?.length > 0 ? (
            <List spacing={3}>
              {reports.map((report, index) => (
                <ListItem key={index}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Text fontSize={"lg"}>{getFileName(report)}</Text>
                    <Link
                      href={report}
                      isExternal
                      color="blue.500"
                      fontWeight="bold"
                      textDecoration="underline"
                    >
                      View
                    </Link>
                  </Box>
                </ListItem>
              ))}
            </List>
          ) : (
            <Text>No reports available.</Text>
          )}

          <PrimarySideHeader mt="20px">Remarks by IPS</PrimarySideHeader>
          <Textarea
            id="remarks"
            name="remarks"
            variant="filled"
            value={ipsRemarks ? ipsRemarks : ""}
            readOnly
          />
          {doctorProvidedOpinion &&
            doctorProvidedOpinion?.add_reports_requested?.length > 0 &&
            doctorProvidedOpinion?.opinion_type === "Need Additional Info" && (
              <>
                <PrimarySideHeader mt="20px">
                  Requested Additional Reports
                </PrimarySideHeader>
                <List spacing={3}>
                  {doctorProvidedOpinion?.add_reports_requested?.map(
                    (report, index) => (
                      <ListItem key={index}>
                        <Box
                          p={3}
                          borderRadius="lg"
                          borderWidth="1px"
                          bg="gray.100"
                        >
                          {renderRequestedReport({
                            ...report,
                            opinion: Array.isArray(report.opinion)
                              ? report.opinion.join(",")
                              : report.opinion,
                          })}
                        </Box>
                      </ListItem>
                    )
                  )}
                </List>
              </>
            )}
          <PrimarySideHeader mt="20px">
            Remarks
          </PrimarySideHeader>
          <Textarea
            id="remarks"
            name="remarks"
            placeholder="Enter your text here..."
            onChange={(e) => setAdditionalReports(e.target.value)}
          />
          
          {attachments.length > 0 && (
            <>
              <PrimarySideHeader mt="20px">Attached Files</PrimarySideHeader>
              <List spacing={3}>
                {attachments.map((file, index) => (
                  <ListItem key={index}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Text fontSize={"lg"}>{file.name}</Text>
                      <Button
                        size="sm"
                        colorScheme="red"
                        variant="ghost"
                        onClick={() => handleRemoveFile(index)}
                        ml={2}
                      >
                        <Icon as={CloseIcon} />
                      </Button>
                    </Box>
                  </ListItem>
                ))}
              </List>
            </>
          )}
          
          <input
            type="file"
            multiple
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
          />
          
          <ModalFooter
            display="flex"
            justifyContent="space-between"
          >
            <PrimaryButton 
              onClick={handleAddReports}
            >
              Add Reports
            </PrimaryButton>
            <PrimaryButton onClick={handleSubmit}>Submit</PrimaryButton>
          </ModalFooter>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ReportsViewModal;
